import type { User } from '../types/user';

export type UserRole = '최고관리자' | '새가족담당' | '재정담당' | '교육담당' | '일반';

export interface PagePermission {
  canAccess: boolean;
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
}

// 페이지별 권한 매트릭스
export const PAGE_PERMISSIONS: Record<string, Record<UserRole, PagePermission>> = {
  dashboard: {
    '최고관리자': { canAccess: true, canRead: true, canWrite: true, canDelete: true },
    '새가족담당': { canAccess: true, canRead: true, canWrite: false, canDelete: false },
    '재정담당': { canAccess: true, canRead: true, canWrite: false, canDelete: false },
    '교육담당': { canAccess: true, canRead: true, canWrite: false, canDelete: false },
    '일반': { canAccess: true, canRead: true, canWrite: false, canDelete: false },
  },
  members: {
    '최고관리자': { canAccess: true, canRead: true, canWrite: true, canDelete: true },
    '새가족담당': { canAccess: true, canRead: true, canWrite: true, canDelete: false },
    '재정담당': { canAccess: true, canRead: true, canWrite: false, canDelete: false },
    '교육담당': { canAccess: true, canRead: true, canWrite: false, canDelete: false },
    '일반': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
  },
  newcomers: {
    '최고관리자': { canAccess: true, canRead: true, canWrite: true, canDelete: true },
    '새가족담당': { canAccess: true, canRead: true, canWrite: true, canDelete: true },
    '재정담당': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
    '교육담당': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
    '일반': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
  },
  visitors: {
    '최고관리자': { canAccess: true, canRead: true, canWrite: true, canDelete: false },
    '새가족담당': { canAccess: true, canRead: true, canWrite: true, canDelete: false },
    '재정담당': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
    '교육담당': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
    '일반': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
  },
  finance: {
    '최고관리자': { canAccess: true, canRead: true, canWrite: true, canDelete: true },
    '새가족담당': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
    '재정담당': { canAccess: true, canRead: true, canWrite: true, canDelete: true },
    '교육담당': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
    '일반': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
  },
  visitations: {
    '최고관리자': { canAccess: true, canRead: true, canWrite: true, canDelete: true },
    '새가족담당': { canAccess: true, canRead: true, canWrite: true, canDelete: false },
    '재정담당': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
    '교육담당': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
    '일반': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
  },
  discipleship: {
    '최고관리자': { canAccess: true, canRead: true, canWrite: true, canDelete: true },
    '새가족담당': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
    '재정담당': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
    '교육담당': { canAccess: true, canRead: true, canWrite: true, canDelete: true },
    '일반': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
  },
  admin: {
    '최고관리자': { canAccess: true, canRead: true, canWrite: true, canDelete: true },
    '새가족담당': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
    '재정담당': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
    '교육담당': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
    '일반': { canAccess: false, canRead: false, canWrite: false, canDelete: false },
  },
};

// 권한 확인 유틸리티 함수들
export const checkPermission = (user: User | null, page: string): PagePermission => {
  if (!user || !user.role) {
    return { canAccess: false, canRead: false, canWrite: false, canDelete: false };
  }

  const pagePermissions = PAGE_PERMISSIONS[page];
  if (!pagePermissions) {
    return { canAccess: false, canRead: false, canWrite: false, canDelete: false };
  }

  return pagePermissions[user.role] || { canAccess: false, canRead: false, canWrite: false, canDelete: false };
};

export const canAccessPage = (user: User | null, page: string): boolean => {
  return checkPermission(user, page).canAccess;
};

export const canReadPage = (user: User | null, page: string): boolean => {
  return checkPermission(user, page).canRead;
};

export const canWritePage = (user: User | null, page: string): boolean => {
  return checkPermission(user, page).canWrite;
};

export const canDeleteFromPage = (user: User | null, page: string): boolean => {
  return checkPermission(user, page).canDelete;
};

// 관리자 권한 확인
export const isAdmin = (user: User | null): boolean => {
  return user?.role === '최고관리자';
};

export const isSuperAdmin = (user: User | null): boolean => {
  return user?.role === '최고관리자';
};

// 권한별 한글 이름
export const ROLE_NAMES: Record<UserRole, string> = {
  '최고관리자': '최고관리자',
  '새가족담당': '새가족담당',
  '재정담당': '재정담당',
  '교육담당': '교육담당',
  '일반': '일반',
};

// 접근 가능한 메뉴 목록 가져오기
export const getAccessibleMenus = (user: User | null): string[] => {
  if (!user) return [];

  const accessibleMenus: string[] = [];

  Object.keys(PAGE_PERMISSIONS).forEach(page => {
    if (canAccessPage(user, page)) {
      accessibleMenus.push(page);
    }
  });

  return accessibleMenus;
};
